import { useEffect, useMemo, useReducer, useState, type FormEvent } from "react";
import { DEFAULT_TEMPLATE_BY_CONTENT_TYPE } from "@contentgen/shared";
import { DeliverableParamsEditor } from "../components/DeliverableParamsEditor.js";
import { OutputPreview } from "../components/OutputPreview.js";
import { RatingControls } from "../components/RatingControls.js";
import { RegeneratePanel } from "../components/RegeneratePanel.js";
import { RoutePicker } from "../components/RoutePicker.js";
import { StatusChip } from "../components/StatusChip.js";
import { api, ApiError } from "../lib/api.js";
import { hashForRoute } from "../lib/hashRoute.js";
import { buildParams, formFromParams, type DeliverableParamForm } from "../lib/deliverableParams.js";
import {
  initRatingState,
  nextRating,
  ratingReducer,
  ratingStateFor,
} from "../lib/ratingState.js";
import {
  NON_TERMINAL_JOB_STATUSES,
  type Deliverable,
  type DeliverableDetail,
  type DeliverableStatus,
  type Item,
  type Job,
  type PromptTemplate,
  type Rating,
} from "../lib/types.js";
import { usePolling } from "../lib/usePolling.js";
import { ROUTE_TITLES, formatPageTitle, useDocumentTitle } from "../lib/pageTitle.js";

const CONTENT_TYPES: Deliverable["contentType"][] = ["text", "image", "svg", "audio", "video"];

/** The seeded outline template id (shared/src/templateSeed.ts). An accepted
 *  deliverable on this template can be fanned out into page items. */
const PICTURE_BOOK_OUTLINE_TEMPLATE_ID = "text/picture-book-outline";

const inputCls =
  "w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 " +
  "outline-none focus:ring-2 focus:ring-(--accent) dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100";

const btnPrimary =
  "shrink-0 rounded-md bg-(--accent) px-4 py-2 text-sm font-medium text-(--accent-contrast) " +
  "hover:bg-(--accent-hover) disabled:opacity-50";

const btnGhost =
  "shrink-0 rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 " +
  "disabled:opacity-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800";

function jobIsNonTerminal(job: Job): boolean {
  return (NON_TERMINAL_JOB_STATUSES as readonly string[]).includes(job.status);
}

function JobHeader({ job }: { job: Job }) {
  return (
    <div className="flex flex-wrap items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
      <StatusChip status={job.status} label={job.status} />
      <span className="font-medium text-gray-700 dark:text-gray-300">{job.routeId}</span>
      <span>{new Date(job.createdAt).toLocaleString()}</span>
      {typeof job.usd === "number" && <span>${job.usd.toFixed(4)}</span>}
      {job.error && (
        <span className="text-red-600 dark:text-red-400">
          {job.error.code}: {job.error.message}
          {job.error.code === "VENDOR_NOT_CONFIGURED" &&
            " - run scripts/set-vendor-key.ps1 for this provider"}
        </span>
      )}
    </div>
  );
}

interface UpstreamState {
  alias: string;
  deliverableId: string;
  key: string;
  status: DeliverableStatus;
}

function DeliverableCard({
  projectId,
  deliverable,
  templates,
  onUpdated,
}: {
  projectId: string;
  deliverable: Deliverable;
  templates: PromptTemplate[];
  onUpdated: (d: Deliverable) => void;
}) {
  const [routeId, setRouteId] = useState(deliverable.routeId ?? "");
  const [detail, setDetail] = useState<DeliverableDetail | null>(null);
  const [ratings, dispatch] = useReducer(ratingReducer, []);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [editing, setEditing] = useState(false);
  const [editInstructions, setEditInstructions] = useState(deliverable.instructions);
  const [editRouteId, setEditRouteId] = useState(deliverable.routeId ?? "");
  const [editTemplateId, setEditTemplateId] = useState(
    deliverable.templateId ?? DEFAULT_TEMPLATE_BY_CONTENT_TYPE[deliverable.contentType],
  );
  const [editVariants, setEditVariants] = useState(deliverable.variantCount);
  const [editParamForm, setEditParamForm] = useState<DeliverableParamForm>(() =>
    formFromParams(deliverable.contentType, deliverable.params),
  );
  const [editBusy, setEditBusy] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);

  // Upstream deliverables this one dependsOn, resolved to their live status so
  // the card can block generation until every one is accepted.
  const [upstreams, setUpstreams] = useState<UpstreamState[]>([]);

  const [expandVariants, setExpandVariants] = useState(1);
  const [expandBusy, setExpandBusy] = useState(false);
  const [expandError, setExpandError] = useState<string | null>(null);
  const [expandResult, setExpandResult] = useState<{ createdItems: number; skipped: number } | null>(
    null,
  );

  async function load() {
    try {
      const d = await api<DeliverableDetail>(`/api/deliverables/${deliverable.deliverableId}`);
      setDetail(d);
      dispatch({ type: "hydrate", outputs: initRatingState(d.outputs) });
      onUpdated(d.deliverable);
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) return;
      setError(err instanceof Error ? err.message : "Could not load this deliverable.");
    }
  }

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deliverable.deliverableId]);

  // Keep this card live while any of its jobs is still working.
  const anyJobInFlight = useMemo(
    () => (detail?.jobs ?? []).some(jobIsNonTerminal),
    [detail],
  );
  usePolling(() => load(), { enabled: anyJobInFlight });

  const dependsOn = deliverable.dependsOn ?? [];
  const dependsOnKey = dependsOn.map((d) => d.deliverableId).join(",");
  useEffect(() => {
    if (dependsOn.length === 0) {
      setUpstreams([]);
      return;
    }
    let cancelled = false;
    void Promise.all(
      dependsOn.map(async (dep): Promise<UpstreamState> => {
        try {
          const d = await api<DeliverableDetail>(`/api/deliverables/${dep.deliverableId}`);
          return {
            alias: dep.alias,
            deliverableId: dep.deliverableId,
            key: d.deliverable.key,
            status: d.deliverable.status,
          };
        } catch {
          // Fail closed: an upstream we cannot resolve counts as not ready.
          return { alias: dep.alias, deliverableId: dep.deliverableId, key: dep.alias, status: "pending" };
        }
      }),
    ).then((resolved) => {
      if (!cancelled) setUpstreams(resolved);
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dependsOnKey]);

  const blockingUpstreams = upstreams.filter((u) => u.status !== "accepted");
  const waitingOnUpstream = blockingUpstreams.length > 0;

  async function run<T>(fn: () => Promise<T>) {
    setBusy(true);
    setError(null);
    try {
      await fn();
    } catch (err) {
      setError(err instanceof Error ? err.message : "That action failed.");
    } finally {
      setBusy(false);
    }
  }

  function handleGenerate() {
    void run(async () => {
      await api(`/api/deliverables/${deliverable.deliverableId}/generate`, {
        method: "POST",
        json: routeId ? { routeId } : {},
      });
      await load();
    });
  }

  function handleRegenerate(opts: { routeId?: string; feedbackText: string }) {
    void run(async () => {
      await api(`/api/deliverables/${deliverable.deliverableId}/generate`, {
        method: "POST",
        json: { routeId: opts.routeId, feedbackText: opts.feedbackText },
      });
      await load();
    });
  }

  function handleSaveEdit(e: FormEvent) {
    e.preventDefault();
    let params: Record<string, unknown>;
    try {
      params = buildParams(deliverable.contentType, editParamForm);
    } catch (err) {
      setEditError(err instanceof Error ? err.message : "Those parameters are not valid.");
      return;
    }
    setEditBusy(true);
    setEditError(null);
    void (async () => {
      try {
        const updated = await api<Deliverable>(`/api/deliverables/${deliverable.deliverableId}`, {
          method: "PATCH",
          json: {
            instructions: editInstructions,
            templateId: editTemplateId || undefined,
            routeId: editRouteId || undefined,
            variantCount: editVariants,
            params,
          },
        });
        onUpdated(updated);
        setRouteId(updated.routeId ?? "");
        setEditing(false);
        await load();
      } catch (err) {
        setEditError(err instanceof Error ? err.message : "Could not save this deliverable.");
      } finally {
        setEditBusy(false);
      }
    })();
  }

  function handleRate(outputId: string, clicked: Rating) {
    const current = ratingStateFor(ratings, outputId);
    const resolved = nextRating(current.rating, clicked);
    dispatch({ type: "rate", outputId, rating: clicked });
    void run(async () => {
      await api(`/api/outputs/${outputId}/rate`, {
        method: "POST",
        json: { rating: resolved, comment: current.comment || undefined },
      });
    });
  }

  function handleComment(outputId: string, comment: string) {
    const current = ratingStateFor(ratings, outputId);
    dispatch({ type: "comment", outputId, comment });
    if (!current.rating) return; // the server drops a comment with no rating
    void run(async () => {
      await api(`/api/outputs/${outputId}/rate`, {
        method: "POST",
        json: { rating: current.rating, comment: comment || undefined },
      });
    });
  }

  function handleChoose(outputId: string, reason: string) {
    dispatch({ type: "choose", outputId, reason });
    void run(async () => {
      await api(`/api/outputs/${outputId}/choose`, { method: "POST", json: { reason } });
      await load();
    });
  }

  function handleAccept(outputId: string) {
    void run(async () => {
      await api(`/api/deliverables/${deliverable.deliverableId}/accept`, {
        method: "POST",
        json: { outputId },
      });
      await load();
    });
  }

  async function handleExpand() {
    if (expandBusy) return;
    setExpandBusy(true);
    setExpandError(null);
    setExpandResult(null);
    try {
      const res = await api<{ createdItems: number; createdDeliverables: number; skipped: number }>(
        `/api/deliverables/${deliverable.deliverableId}/expand`,
        { method: "POST", json: { variantCount: expandVariants } },
      );
      setExpandResult({ createdItems: res.createdItems, skipped: res.skipped });
    } catch (err) {
      setExpandError(err instanceof Error ? err.message : "Could not expand this outline.");
    } finally {
      setExpandBusy(false);
    }
  }

  const jobs = detail?.jobs ?? [];
  const outputs = detail?.outputs ?? [];
  const newestRouteId = jobs[0]?.routeId;
  const templatesForType = templates.filter((t) => t.contentType === deliverable.contentType);
  const canExpand =
    deliverable.templateId === PICTURE_BOOK_OUTLINE_TEMPLATE_ID && deliverable.status === "accepted";

  return (
    <div className="rounded-md border border-gray-200 p-4 dark:border-gray-800">
      <div className="flex items-center justify-between gap-2">
        <div>
          <div className="font-medium text-gray-900 dark:text-gray-100">{deliverable.key}</div>
          <div className="text-xs text-gray-500 dark:text-gray-400">
            {deliverable.contentType} - {deliverable.variantCount} variant
            {deliverable.variantCount === 1 ? "" : "s"}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            className="text-xs text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
            onClick={() => setEditing((v) => !v)}
          >
            {editing ? "Close" : "Edit"}
          </button>
          <StatusChip status={deliverable.status} label={deliverable.status} />
        </div>
      </div>

      {editing && (
        <form
          onSubmit={handleSaveEdit}
          className="mt-3 space-y-3 rounded-md border border-gray-200 p-3 dark:border-gray-800"
        >
          <div>
            <label className="mb-1 block text-xs text-gray-500 dark:text-gray-400">Instructions</label>
            <textarea
              rows={2}
              className={inputCls}
              value={editInstructions}
              onChange={(e) => setEditInstructions(e.target.value)}
            />
          </div>
          <div className="flex flex-wrap items-end gap-2">
            <div className="min-w-56 flex-1">
              <label className="mb-1 block text-xs text-gray-500 dark:text-gray-400">Route</label>
              <RoutePicker
                contentType={deliverable.contentType}
                value={editRouteId}
                onChange={setEditRouteId}
              />
            </div>
            <div className="min-w-40 flex-1">
              <label className="mb-1 block text-xs text-gray-500 dark:text-gray-400">Template</label>
              <select
                className={inputCls}
                value={editTemplateId}
                onChange={(e) => setEditTemplateId(e.target.value)}
              >
                {templatesForType.length === 0 && (
                  <option value={editTemplateId}>{editTemplateId || "(default)"}</option>
                )}
                {templatesForType.map((t) => (
                  <option key={t.templateId} value={t.templateId}>
                    {t.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="mb-1 block text-xs text-gray-500 dark:text-gray-400">Variants</label>
              <input
                type="number"
                min={1}
                max={4}
                className="w-20 rounded-md border border-gray-300 bg-white px-2 py-2 text-sm text-gray-900 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
                value={editVariants}
                onChange={(e) => setEditVariants(Math.min(4, Math.max(1, Number(e.target.value) || 1)))}
              />
            </div>
          </div>
          <DeliverableParamsEditor
            contentType={deliverable.contentType}
            form={editParamForm}
            onChange={setEditParamForm}
          />
          {editError && <p className="text-sm text-red-600 dark:text-red-400">{editError}</p>}
          <button type="submit" className={btnPrimary} disabled={editBusy}>
            {editBusy ? "Saving..." : "Save deliverable"}
          </button>
        </form>
      )}

      {upstreams.length > 0 && (
        <div className="mt-3 rounded-md border border-gray-200 p-2 text-xs dark:border-gray-800">
          <div className="mb-1 text-gray-500 dark:text-gray-400">Depends on</div>
          <ul className="space-y-1">
            {upstreams.map((u) => (
              <li key={u.deliverableId} className="flex items-center gap-2">
                <span className="text-gray-700 dark:text-gray-300">{u.key}</span>
                <StatusChip status={u.status} label={u.status} />
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="mt-3 flex flex-wrap items-end gap-2">
        <div className="min-w-56 flex-1">
          <label className="mb-1 block text-xs text-gray-500 dark:text-gray-400">Route</label>
          <RoutePicker contentType={deliverable.contentType} value={routeId} onChange={setRouteId} />
        </div>
        <button
          type="button"
          className={btnPrimary}
          disabled={busy || waitingOnUpstream}
          onClick={handleGenerate}
        >
          {busy ? "Starting..." : jobs.length === 0 ? "Generate" : "Generate again"}
        </button>
      </div>
      {waitingOnUpstream && (
        <p className="mt-1 text-xs text-amber-700 dark:text-amber-400">
          Waiting on: {blockingUpstreams.map((u) => u.key).join(", ")}
        </p>
      )}

      {canExpand && (
        <div className="mt-3 rounded-md border border-gray-200 p-3 dark:border-gray-800">
          <div className="flex flex-wrap items-end gap-2">
            <div>
              <label className="mb-1 block text-xs text-gray-500 dark:text-gray-400">
                Variants per page
              </label>
              <input
                type="number"
                min={1}
                max={4}
                className="w-20 rounded-md border border-gray-300 bg-white px-2 py-2 text-sm text-gray-900 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
                value={expandVariants}
                onChange={(e) =>
                  setExpandVariants(Math.min(4, Math.max(1, Number(e.target.value) || 1)))
                }
              />
            </div>
            <button type="button" className={btnGhost} disabled={expandBusy} onClick={() => void handleExpand()}>
              {expandBusy ? "Expanding..." : "Expand into pages"}
            </button>
          </div>
          {expandResult && (
            <p className="mt-2 text-xs text-gray-600 dark:text-gray-400">
              Created {expandResult.createdItems} page(s)
              {expandResult.skipped > 0 ? `, skipped ${expandResult.skipped} that already existed` : ""}.{" "}
              <a
                href={hashForRoute({ kind: "book", projectId })}
                className="text-(--accent) hover:underline"
              >
                Open the book view
              </a>
            </p>
          )}
          {expandError && <p className="mt-2 text-xs text-red-600 dark:text-red-400">{expandError}</p>}
        </div>
      )}

      {jobs.map((job) => {
        const jobOutputs = outputs
          .filter((o) => o.jobId === job.jobId)
          .sort((a, b) => a.variantIndex - b.variantIndex);
        return (
          <div key={job.jobId} className="mt-4 border-t border-gray-100 pt-3 dark:border-gray-800">
            <JobHeader job={job} />
            {jobOutputs.length > 0 && (
              <div className="mt-3 grid gap-4 sm:grid-cols-2">
                {jobOutputs.map((o) => (
                  <div key={o.outputId} className="rounded-md border border-gray-200 p-3 dark:border-gray-800">
                    {jobOutputs.length > 1 && (
                      <div className="mb-1 text-xs text-gray-400">Variant {o.variantIndex + 1}</div>
                    )}
                    <OutputPreview output={o} />
                    <RatingControls
                      state={ratingStateFor(ratings, o.outputId)}
                      hasVariants={job.variantCount > 1}
                      isAccepted={detail?.deliverable.acceptedOutputId === o.outputId}
                      busy={busy}
                      onRate={(r) => handleRate(o.outputId, r)}
                      onCommentBlur={(c) => handleComment(o.outputId, c)}
                      onChoose={(reason) => handleChoose(o.outputId, reason)}
                      onAccept={() => handleAccept(o.outputId)}
                    />
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}

      {jobs.length > 0 && (
        <RegeneratePanel
          contentType={deliverable.contentType}
          currentRouteId={newestRouteId}
          outputs={outputs}
          busy={busy}
          onRegenerate={handleRegenerate}
        />
      )}

      {error && <p className="mt-2 text-sm text-red-600 dark:text-red-400">{error}</p>}
    </div>
  );
}

function AddDeliverableForm({
  projectId,
  itemId,
  templates,
  existingKeys,
  onAdded,
}: {
  projectId: string;
  itemId: string;
  templates: PromptTemplate[];
  existingKeys: string[];
  onAdded: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [key, setKey] = useState("");
  const [contentType, setContentType] = useState<Deliverable["contentType"]>("text");
  const [templateId, setTemplateId] = useState<string>(DEFAULT_TEMPLATE_BY_CONTENT_TYPE.text);
  const [routeId, setRouteId] = useState("");
  const [variants, setVariants] = useState(1);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const templatesForType = useMemo(
    () => templates.filter((t) => t.contentType === contentType),
    [templates, contentType],
  );

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    const trimmed = key.trim();
    if (!trimmed) return;
    if (existingKeys.includes(trimmed)) {
      setError("This item already has a deliverable with that key.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await api(`/api/projects/${projectId}/items/${itemId}/deliverables`, {
        method: "POST",
        json: {
          key: trimmed,
          contentType,
          templateId: templateId || undefined,
          routeId: routeId || undefined,
          variantCount: variants,
        },
      });
      setKey("");
      setOpen(false);
      onAdded();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not add the deliverable.");
    } finally {
      setBusy(false);
    }
  }

  if (!open) {
    return (
      <button type="button" className={btnGhost} onClick={() => setOpen(true)}>
        Add deliverable
      </button>
    );
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="flex flex-wrap items-end gap-2 rounded-md border border-gray-200 p-3 dark:border-gray-800"
    >
      <div>
        <label className="mb-1 block text-xs text-gray-500 dark:text-gray-400">Key</label>
        <input
          className={inputCls}
          value={key}
          onChange={(e) => setKey(e.target.value)}
          placeholder="instructions"
        />
      </div>
      <div>
        <label className="mb-1 block text-xs text-gray-500 dark:text-gray-400">Content type</label>
        <select
          className={inputCls}
          value={contentType}
          onChange={(e) => {
            const ct = e.target.value as Deliverable["contentType"];
            setContentType(ct);
            setTemplateId(DEFAULT_TEMPLATE_BY_CONTENT_TYPE[ct]);
          }}
        >
          {CONTENT_TYPES.map((ct) => (
            <option key={ct} value={ct}>
              {ct}
            </option>
          ))}
        </select>
      </div>
      <div className="min-w-40">
        <label className="mb-1 block text-xs text-gray-500 dark:text-gray-400">Template</label>
        <select
          className={inputCls}
          value={templateId}
          onChange={(e) => setTemplateId(e.target.value)}
        >
          {templatesForType.length === 0 && (
            <option value={templateId}>{templateId || "(default)"}</option>
          )}
          {templatesForType.map((t) => (
            <option key={t.templateId} value={t.templateId}>
              {t.name}
            </option>
          ))}
        </select>
      </div>
      <div className="min-w-56 flex-1">
        <label className="mb-1 block text-xs text-gray-500 dark:text-gray-400">Route</label>
        <RoutePicker contentType={contentType} value={routeId} onChange={setRouteId} />
      </div>
      <div>
        <label className="mb-1 block text-xs text-gray-500 dark:text-gray-400">Variants</label>
        <input
          type="number"
          min={1}
          max={4}
          className="w-20 rounded-md border border-gray-300 bg-white px-2 py-2 text-sm text-gray-900 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
          value={variants}
          onChange={(e) => setVariants(Math.min(4, Math.max(1, Number(e.target.value) || 1)))}
        />
      </div>
      <button type="submit" className={btnPrimary} disabled={busy || !key.trim()}>
        {busy ? "Adding..." : "Add"}
      </button>
      <button type="button" className={btnGhost} onClick={() => setOpen(false)}>
        Cancel
      </button>
      {error && <p className="w-full text-sm text-red-600 dark:text-red-400">{error}</p>}
    </form>
  );
}

export function ItemDetail({ projectId, itemId }: { projectId: string; itemId: string }) {
  const [item, setItem] = useState<Item | null>(null);
  useDocumentTitle(
    item && item.itemId === itemId ? formatPageTitle(item.title) : formatPageTitle(ROUTE_TITLES.itemDetail),
  );
  const [deliverables, setDeliverables] = useState<Deliverable[]>([]);
  const [templates, setTemplates] = useState<PromptTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [title, setTitle] = useState("");
  const [brief, setBrief] = useState("");
  const [savingItem, setSavingItem] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const [loadedItem, page] = await Promise.all([
        api<Item>(`/api/projects/${projectId}/items/${itemId}`),
        api<{ items: Deliverable[] }>(`/api/projects/${projectId}/items/${itemId}/deliverables`),
      ]);
      setItem(loadedItem);
      setTitle(loadedItem.title);
      setBrief(loadedItem.brief);
      setDeliverables(page.items);
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) return;
      setError(err instanceof Error ? err.message : "Could not load this item.");
    } finally {
      setLoading(false);
    }
  }

  async function reloadDeliverables() {
    try {
      const page = await api<{ items: Deliverable[] }>(
        `/api/projects/${projectId}/items/${itemId}/deliverables`,
      );
      setDeliverables(page.items);
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) return;
      setError(err instanceof Error ? err.message : "Could not reload deliverables.");
    }
  }

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, itemId]);

  useEffect(() => {
    void api<{ items: PromptTemplate[] }>("/api/templates")
      .then((res) => setTemplates(res.items))
      .catch(() => setTemplates([]));
  }, []);

  async function handleSaveItem(e: FormEvent) {
    e.preventDefault();
    setSavingItem(true);
    setSaveError(null);
    try {
      const updated = await api<Item>(`/api/projects/${projectId}/items/${itemId}`, {
        method: "PATCH",
        json: { title, brief },
      });
      setItem(updated);
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : "Could not save this item.");
    } finally {
      setSavingItem(false);
    }
  }

  if (loading) {
    return <p className="p-6 text-sm text-gray-500 dark:text-gray-400">Loading...</p>;
  }
  if (error || !item) {
    return <p className="p-6 text-sm text-red-600 dark:text-red-400">{error ?? "Item not found."}</p>;
  }

  return (
    <div className="mx-auto max-w-4xl p-6">
      <form onSubmit={(e) => void handleSaveItem(e)} className="space-y-3">
        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">Title</label>
          <input className={inputCls} value={title} onChange={(e) => setTitle(e.target.value)} />
        </div>
        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">Brief</label>
          <textarea rows={3} className={inputCls} value={brief} onChange={(e) => setBrief(e.target.value)} />
        </div>
        {saveError && <p className="text-sm text-red-600 dark:text-red-400">{saveError}</p>}
        <button type="submit" className={btnPrimary} disabled={savingItem}>
          {savingItem ? "Saving..." : "Save"}
        </button>
      </form>

      <h2 className="mt-6 text-sm font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
        Deliverables
      </h2>

      <div className="mt-2">
        <AddDeliverableForm
          projectId={projectId}
          itemId={itemId}
          templates={templates}
          existingKeys={deliverables.map((d) => d.key)}
          onAdded={() => void reloadDeliverables()}
        />
      </div>

      <div className="mt-3 space-y-3">
        {deliverables.map((d) => (
          <DeliverableCard
            key={d.deliverableId}
            projectId={projectId}
            deliverable={d}
            templates={templates}
            onUpdated={(updated) =>
              setDeliverables((prev) => prev.map((x) => (x.deliverableId === updated.deliverableId ? updated : x)))
            }
          />
        ))}
        {deliverables.length === 0 && (
          <p className="text-sm text-gray-500 dark:text-gray-400">
            No deliverables yet - add one above.
          </p>
        )}
      </div>
    </div>
  );
}
